/*
 * Purpose: Flow survey row cards — ready, building, stats
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Hammer,
  Layers,
  Link2,
  Play,
  Wrench,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { FlowSurveyItem } from '@/lib/flowSurvey';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { useWorkflowStore } from '@/stores/workflowStore';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { cn } from '@/lib/utils';

function statusMeta(status: FlowSurveyItem['status']) {
  switch (status) {
    case 'ready':
      return {
        label: 'Ready',
        icon: CheckCircle2,
        badge: 'border-[hsl(var(--ab-green-core)/0.4)] text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))]',
      };
    case 'building':
      return {
        label: 'Still building',
        icon: Hammer,
        badge: 'border-amber-500/40 text-amber-700 dark:text-amber-400',
      };
    default:
      return {
        label: 'Running',
        icon: Play,
        badge: 'border-[hsl(var(--ab-green-core)/0.4)]',
      };
  }
}

export function FlowSurveyCard({ item, compact }: { item: FlowSurveyItem; compact?: boolean }) {
  const setCurrentWorkflow = useWorkflowStore((s) => s.setCurrentWorkflow);
  const meta = statusMeta(item.status);
  const Icon = meta.icon;
  const flowId = item.workflow.id;
  const mapHref = `${REMEDIATION.mission}?flow=${encodeURIComponent(flowId)}`;
  const flowsHref = `${REMEDIATION.flows}?flow=${encodeURIComponent(flowId)}`;

  const open = () => setCurrentWorkflow(item.workflow);

  return (
    <Card
      className={cn(
        'p-4 transition-colors hover:border-primary/30',
        item.status === 'running' && 'border-[hsl(var(--ab-green-core)/0.3)]'
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Icon className="h-5 w-5 shrink-0 mt-0.5 text-muted-foreground" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-medium text-foreground truncate">{item.workflow.name}</h3>
              <Badge variant="outline" className={cn('text-[10px]', meta.badge)}>
                {meta.label}
              </Badge>
            </div>
            {!compact && (
              <p className="text-xs text-muted-foreground mt-1">
                {item.nodeCount} blocks · {item.linkCount} links
                {item.errorCount > 0 ? ` · ${item.errorCount} issue${item.errorCount === 1 ? '' : 's'}` : ''}
                {item.warningCount > 0 && item.errorCount === 0
                  ? ` · ${item.warningCount} warning${item.warningCount === 1 ? '' : 's'}`
                  : ''}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Updated {item.updatedAt.toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button size="sm" actionTier="secondary" className="h-8 text-xs" asChild>
            <Link to={mapHref} onClick={open}>
              {item.errorCount > 0 ? (
                <>
                  <Wrench className="h-3.5 w-3.5 mr-1" />
                  Fix {item.errorCount} issue{item.errorCount === 1 ? '' : 's'}
                </>
              ) : item.status === 'building' ? (
                <>
                  <Wrench className="h-3.5 w-3.5 mr-1" />
                  Continue
                </>
              ) : (
                <>
                  <Play className="h-3.5 w-3.5 mr-1" />
                  {PRODUCT_TERMS.mission}
                </>
              )}
            </Link>
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" asChild>
            <Link to={flowsHref} onClick={open}>
              Manage
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface FlowSurveySectionProps {
  title: string;
  description: string;
  items: FlowSurveyItem[];
  emptyMessage: string;
  emptyAction?: { label: string; href: string };
}

export function FlowSurveySection({
  title,
  description,
  items,
  emptyMessage,
  emptyAction,
}: FlowSurveySectionProps) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      {items.length === 0 ? (
        <Card className="p-6 text-center border-dashed">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          {emptyAction ? (
            <Button asChild size="sm" actionTier="secondary" className="mt-3">
              <Link to={emptyAction.href}>{emptyAction.label}</Link>
            </Button>
          ) : null}
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((item) => (
            <FlowSurveyCard key={item.workflow.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

export function DashboardStatGrid({
  stats,
}: {
  stats: {
    label: string;
    value: number;
    icon: typeof Layers;
    accent?: string;
  }[];
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card key={stat.label} className="p-4 sm:p-5 space-y-2">
            <Icon className={cn('h-5 w-5', stat.accent ?? 'text-muted-foreground')} />
            <p className="text-xs text-muted-foreground">{stat.label}</p>
            <p className="text-2xl sm:text-3xl font-bold tabular-nums">{stat.value}</p>
          </Card>
        );
      })}
    </div>
  );
}

export function ComposeInProgressBanner({
  outcomeText,
  phase,
}: {
  outcomeText: string;
  phase: string;
}) {
  return (
    <Card className="p-4 border-[hsl(var(--ab-green-teal)/0.35)] bg-[hsl(var(--ab-green-teal)/0.08)]">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <AlertTriangle className="h-5 w-5 shrink-0 text-[hsl(var(--ab-green-deep))] dark:text-[hsl(var(--ab-green-glow))]" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Compose session in progress</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {outcomeText} · phase: {phase.replace(/_/g, ' ')}
            </p>
          </div>
        </div>
        <Button asChild size="sm" actionTier="primary" className="shrink-0">
          <Link to="/compose">Resume {PRODUCT_TERMS.autoBlockFlowComposer}</Link>
        </Button>
      </div>
    </Card>
  );
}

export { Link2 };
